import { defineSlotRecipe } from "@chakra-ui/react";

const tabsSlotRecipe = defineSlotRecipe({
  slots: ["root", "list", "trigger", "content"],
  className: "chakra-tabs",
  base: {
    root: {
      position: "relative",
    },
    list: {
      display: "inline-flex",
      position: "relative",
      isolation: "isolate",
      "--tabs-indicator-shadow": "none",
      "--tabs-indicator-bg": "none",
      "--indicator-color": "transparent",
    },
  },

  variants: {
    stickyOnDesktop: {
      true: {},
      false: {},
    },
    fullWidthOnMobile: {
      true: {},
      false: {},
    },
    variant: {
      standard: {
        root: {
          position: "relative",
          backgroundColor: {
            _light: "base.200",
            _dark: "base.700",
          },
          "&::before": {
            content: '""',
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: {
              base: "var(--tabs-sticky-top-mobile)",
              md: "var(--tabs-sticky-top, 0)",
            },
            backgroundColor: {
              _light: "base.300",
              _dark: "base.700",
            }, // this is important
            "html.light &": {
              backgroundColor: "base.300",
            },
            "html.dark &": {
              backgroundColor: "base.700",
            },
            "[style*='color-scheme: light'] &": {
              backgroundColor: "base.300",
            },
            "[style*='color-scheme: dark'] &": {
              backgroundColor: "base.700",
            },
            maxW: { base: "100%", md: "1200px" },
            mx: "auto",
            zIndex: 15,
            pointerEvents: "none",
          },
        },
        list: {
          position: "sticky",
          top: "var(--tabs-sticky-top, 0)",
          zIndex: 12,
          backgroundColor: {
            _light: "base.300",
            _dark: "base.700",
          },
          marginBottom: "-1px",
          height: "40px",
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          scrollBehavior: "smooth",
          isolation: "isolate",
          gap: 1,
          "&::-webkit-scrollbar": {
            display: { base: "none", md: "auto" },
          },
          scrollbarWidth: { base: "none", md: "auto" },
          msOverflowStyle: { base: "none", md: "auto" },
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "1px",
            backgroundColor: {
              _light: "lines.400",
              _dark: "lines.600",
            },
            "html.light &": {
              backgroundColor: "lines.400",
            },
            "html.dark &": {
              backgroundColor: "lines.600",
            },
            "[style*='color-scheme: light'] &": {
              backgroundColor: "lines.400",
            },
            "[style*='color-scheme: dark'] &": {
              backgroundColor: "lines.600",
            },
            zIndex: 1,
            width: "var(--tabs-content-width, 100%)",
          },
        },
        trigger: {
          outline: "0",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textStyle: "body.small",
          fontSize: "12px",
          height: "2.5rem",
          fontFamily: "inter, sans-serif",
          flex: { base: "0 0 auto", md: "0 0 auto" },
          minWidth: {
            base: "fit-content !important",
            md: "fit-content !important",
          },
          padding: "0.5rem 1rem",
          width: { base: "auto", md: "auto" },
          textAlign: "center",

          border: "1px solid",
          borderBottom: "none",
          borderColor: {
            _light: "lines.400",
            _dark: "lines.600",
          },
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          color: { _light: "action.600", _dark: "action.400" },
          backgroundColor: {
            _light: "base.300",
            _dark: "base.800",
          },
          position: "relative",
          fontWeight: "400",
          _focusVisible: {
            zIndex: 1,
            outline: "2px solid",
            outlineColor: "colorPalette.focusRing",
          },
          _selected: {
            backgroundColor: {
              _light: "base.200",
              _dark: "base.700",
            },
            color: { _light: "base.700", _dark: "base.200" },
            marginBottom: "-5px",
            fontWeight: "600 !important",
            zIndex: 15,
            borderBottom: "1px solid",
            borderBottomColor: {
              _light: "base.200",
              _dark: "base.700",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-2px",
              left: 0,
              right: 0,
              height: "3px",
              zIndex: 20,
            },
          },
          _disabled: {
            cursor: "not-allowed",
            opacity: 0.5,
          },
          _active: {
            color: "base.700",
          },
          _focus: {
            color: "base.700",
            backgroundColor: {
              _light: "base.200",
              _dark: "base.700",
            },
          },
          _hover: {
            backgroundColor: {
              _light: "base.200",
              _dark: "base.700",
            },
            color: { _light: "base.700", _dark: "action.500" },
            _focus: {
              backgroundColor: {
                _light: "base.200",
                _dark: "base.700",
              },
            },
          },
        },
        content: {
          padding: "1rem",
          borderLeft: "1px solid",
          borderRight: "1px solid",
          borderBottom: "1px solid",
          borderTop: "1px solid",
          borderColor: {
            _light: "lines.400",
            _dark: "lines.600",
          },
          borderBottomLeftRadius: "md",
          borderBottomRightRadius: "md",
          backgroundColor:
            "var(--tabs-content-bg, var(--chakra-colors-canvas-default))",
          position: "relative",
          zIndex: 1,
        },
      },
    },
  },

  compoundVariants: [
    {
      stickyOnDesktop: false,
      variant: "standard",
      css: {
        list: {
          position: { base: "sticky", md: "relative" },
          top: {
            base: "var(--tabs-sticky-top-mobile, var(--tabs-sticky-top, 0))",
            md: "auto",
          },
        },
      },
    },
    {
      fullWidthOnMobile: true,
      variant: "standard",
      css: {
        list: {
          width: { base: "100%", md: "auto" },
        },
        trigger: {
          flex: { base: "1", md: "0 0 auto" },
        },
      },
    },
    {
      stickyOnDesktop: false,
      variant: "standard",
      css: {
        content: {
          marginTop: {
            base: "var(--tabs-sticky-top-mobile, var(--tabs-sticky-top, 0))",
            md: "0",
          },
        },
      },
    },
  ],

  defaultVariants: {
    variant: "standard",
    stickyOnDesktop: true,
    fullWidthOnMobile: false,
  },
});

export default tabsSlotRecipe;
